import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-4xl px-4 py-8 mx-auto sm:px-6 lg:px-8">
        {/* Back Navigation */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-orange-400 transition-colors hover:text-orange-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-white">Terms and Conditions</h1>
          <p className="text-xl text-gray-300">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          <div className="space-y-8 text-gray-300">
            
            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">1. Agreement to Terms</h2>
              <p>
                By accessing and using PexiBlock's services, you accept and agree to be bound by the terms and 
                provision of this agreement. These Terms of Service ("Terms") govern your use of our website, 
                services, and applications offered by PexiBlock.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">2. Description of Service</h2>
              <p>
                PexiBlock provides cryptocurrency payment processing services that enable businesses to accept 
                digital currency payments. Our platform facilitates secure, fast, and reliable payment solutions 
                for merchants worldwide.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">3. User Accounts</h2>
              <div className="space-y-4">
                <p>
                  To access certain features of our service, you must register for an account. You agree to:
                </p>
                <ul className="ml-6 space-y-2 list-disc">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Notify us immediately of unauthorized use of your account</li>
                  <li>Accept responsibility for all activities under your account</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">4. Payment Processing</h2>
              <div className="space-y-4">
                <p>
                  Our payment processing services are subject to the following terms:
                </p>
                <ul className="ml-6 space-y-2 list-disc">
                  <li>All transactions are processed in cryptocurrency</li>
                  <li>Transaction fees apply as outlined in our fee schedule</li>
                  <li>Settlements are processed according to our standard timelines</li>
                  <li>Chargebacks and disputes are handled according to cryptocurrency network protocols</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">5. Prohibited Activities</h2>
              <div className="space-y-4">
                <p>
                  You agree not to use our services for:
                </p>
                <ul className="ml-6 space-y-2 list-disc">
                  <li>Illegal activities or transactions</li>
                  <li>Money laundering or terrorist financing</li>
                  <li>Fraud or deceptive practices</li>
                  <li>Violation of any applicable laws or regulations</li>
                  <li>Activities that could harm our reputation or operations</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">6. Security and Compliance</h2>
              <p>
                We implement industry-standard security measures to protect your data and transactions. 
                However, you acknowledge that no system is completely secure, and you use our services at your own risk. 
                We comply with applicable regulations and may require additional verification for compliance purposes.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">7. Intellectual Property</h2>
              <p>
                The PexiBlock service, including its design, functionality, and content, is owned by PexiBlock and 
                protected by copyright, trademark, and other intellectual property laws. You may not reproduce, 
                distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">8. Disclaimer of Warranties</h2>
              <p>
                Our services are provided "as is" without warranties of any kind, either express or implied. 
                We do not guarantee that our services will be uninterrupted, error-free, or completely secure. 
                Cryptocurrency transactions are irreversible, and you assume all risks associated with such transactions.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">9. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, PexiBlock shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages, including without limitation, loss of profits, data, 
                use, or other intangible losses resulting from your use of our services.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">10. Termination</h2>
              <p>
                We may terminate or suspend your account and access to our services immediately, without prior notice 
                or liability, for any reason, including if you breach these Terms. Upon termination, your right to 
                use our services will cease immediately.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">11. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with applicable laws. Any disputes 
                arising from these Terms or your use of our services shall be resolved through binding arbitration.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">12. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify users of material changes 
                by posting the updated Terms on our website. Your continued use of our services after such changes 
                constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="mb-4 text-2xl font-semibold text-white">13. Contact Information</h2>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="p-4 mt-4 rounded-lg bg-slate-800">
                <p><strong>Email:</strong> info@pexipay.com</p>
              </div>
            </section>

          </div>
        </div>

        {/* Footer */}
        <div className="pt-8 mt-12 border-t border-gray-700">
          <div className="text-center">
            <p className="text-sm text-gray-400">
              These terms are effective as of the date last updated above and will remain in effect except with 
              respect to any changes in their provisions in the future.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}